<script setup>
/**
 * The end of a lesson (lesson-redo DESIGN.md E1, E3; API.md 0.2), centred on
 * the same cream paper, under ✦ This spoke to me.
 *
 * Anyone but the sharer: a quiet gold divider with a ✦; the lesson's plain
 * question (ASK_YOU) as the headline; "Draw a card and {Mystic} will read it
 * for where you are."; the button [▢ the Mystic's square] Draw a card │ 10 ✦
 * (the price in its own segment); "Talk it over with {Mystic} instead". The
 * server's `ending` words win. Emits `draw` { how: 'draw' | 'talk', href }:
 * the page opens a fresh session with the Mystic (E2); nothing is charged
 * until the card is picked.
 *
 * The sharer: how far the lesson has gone ("Your lesson has spoken to 4
 * people." / "1 person drew their own card after reading it.", calm at
 * zero), and ✦ Ask more about your reading, wearing the Mystic's square.
 * Emits `own`.
 *
 * One draw per lesson per seeker (2026-10-28, API.md 0.2 `my_draw`): once
 * they drew, "Your card: {card} ›" with its thumbnail stands in place of
 * Draw a card and reopens that reading (`draw` { how: 'draw', href, mine }).
 */
import { computed, ref, watch } from 'vue'
import { endingWords, mysticOf, myDrawOf, sharerLines, SHARER_ASK } from '@/utils/lesson'

const props = defineProps({ lesson: { type: Object, required: true } })
const emit = defineEmits(['draw', 'own'])

const own = computed(() => !!(props.lesson && props.lesson.invite && props.lesson.invite.own))
const words = computed(() => endingWords(props.lesson))
const mystic = computed(() => mysticOf(props.lesson))
const lines = computed(() => sharerLines(props.lesson && props.lesson.invite))
const mine = computed(() => myDrawOf(props.lesson))
const mineName = computed(() => (mine.value ? `${mine.value.card}${mine.value.reversed ? ', reversed' : ''}` : ''))
const thumbFailed = ref(false)
watch(() => mine.value && mine.value.image, () => { thumbFailed.value = false })
const porFailed = ref(false)
watch(() => mystic.value.avatar, () => { porFailed.value = false })
const initial = computed(() => (mystic.value.name || '✦').trim().charAt(0).toUpperCase())

const lit = ref('')
function draw() { lit.value = 'draw'; emit('draw', { how: 'draw', href: words.value.drawHref }) }
function talk() { emit('draw', { how: 'talk', href: words.value.talkHref }) }
function reopen() { lit.value = 'mine'; emit('draw', { how: 'draw', href: mine.value.href || words.value.drawHref, mine: true }) }
watch(() => props.lesson && props.lesson.short_uuid, () => { lit.value = '' })
</script>

<template>
  <section v-if="!own" class="yt" data-testid="lesson-ending" :aria-label="words.button">
    <div class="yt-div" aria-hidden="true"><span>✦</span></div>
    <h2 class="yt-h" data-testid="lesson-ending-headline">{{ words.headline }}</h2>
    <p v-if="!mine" class="yt-line" data-testid="lesson-ending-line">{{ words.line }}</p>
    <p v-else class="yt-line" data-testid="lesson-ending-line">{{ `${mystic.name} read a card for you.` }}</p>
    <button v-if="mine" type="button" class="yt-mine" :class="{ lit: lit === 'mine' }" data-testid="lesson-my-card"
            :aria-label="`Your card: ${mineName}. Open the reading`" @click="reopen">
      <span class="yt-thumb" :class="{ rev: mine.reversed }" aria-hidden="true">
        <img v-if="mine.image && !thumbFailed" :src="mine.image" alt="" @error="thumbFailed = true" />
        <span v-else class="yt-thumb-ph">✦</span>
      </span>
      <span class="yt-mine-t"><span class="yt-mine-k">Your card:</span> {{ mineName }}</span>
      <span class="yt-mine-go" aria-hidden="true">{{ '\u00a0›' }}</span>
    </button>
    <button v-else type="button" class="yt-btn" :class="{ lit: lit === 'draw' }" :aria-label="words.aria" data-testid="lesson-draw" @click="draw">
      <span class="sqm" aria-hidden="true">
        <img v-if="mystic.avatar && !porFailed" :src="mystic.avatar" alt="" @error="porFailed = true" />
        <span v-else class="sqm-ph">{{ initial }}</span>
      </span>
      <span class="yt-b-t">{{ words.button }}</span>
      <span class="yt-price">{{ words.price }} ✦</span>
    </button>
    <button type="button" class="yt-talk" data-testid="lesson-talk" @click="talk">{{ words.talk }}</button>
  </section>

  <section v-else class="own" data-testid="lesson-ending" aria-label="Your lesson">
    <div data-testid="lesson-own-lines">
      <p class="own-n">{{ lines[0] }}</p>
      <p v-if="lines[1]" class="own-d">{{ lines[1] }}</p>
    </div>
    <button type="button" class="own-b" data-testid="lesson-own-ask" @click="emit('own', { words: '' })">
      <span class="por" aria-hidden="true">
        <img v-if="mystic.avatar && !porFailed" :src="mystic.avatar" alt="" @error="porFailed = true" />
        <span v-else class="sqm-ph">{{ initial }}</span>
      </span>
      <span>✦ {{ SHARER_ASK }}</span>
    </button>
  </section>
</template>

<style scoped>
.yt, .own {
  --gold: #c9a94e; --golddk: #8a6a2a; --ink: #2a1845; --ink2: #3d2f55; --amethyst: #4e2776; --plum: #2a1845;
  --display: 'Letter Display', 'Cinzel', Georgia, serif; --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  --ui: Inter, system-ui, -apple-system, 'Segoe UI', sans-serif;
}
.yt *, .own * { box-sizing: border-box; }
.yt img, .own img { display: block; }
/* E1: centred on the paper. */
.yt { text-align: center; margin: 0 22px; padding: 6px 0 36px; color: var(--ink); }
.yt-div { display: flex; align-items: center; gap: 14px; margin: 4px 0 22px; color: var(--gold); font-size: 13px; }
.yt-div::before, .yt-div::after { content: ''; flex: 1; height: 1px; background: rgba(201, 169, 78, .45); }
.yt-h { margin: 0 auto; max-width: 22ch; font: 600 30px/1.15 var(--serif); color: var(--ink); text-wrap: balance; overflow-wrap: anywhere; }
.yt-line { margin: 10px auto 22px; max-width: 34ch; font: 500 17px/1.45 var(--serif); color: var(--ink2); }
.yt-btn {
  display: inline-flex; align-items: center; max-width: 100%; min-height: 58px; padding: 0; overflow: hidden; border-radius: 16px; cursor: pointer; text-align: left;
  border: 1px solid rgba(231, 201, 131, .7); background: linear-gradient(180deg, #7a47b3, #4f2785); color: #ffe9b8;
  box-shadow: 0 14px 28px -14px rgba(78, 39, 118, .95); transition: filter .2s, transform .2s, box-shadow .2s;
}
.yt-btn:hover { filter: brightness(1.08); }
.yt-btn:focus-visible { outline: 2px solid var(--gold); outline-offset: 3px; }
.yt-btn.lit { transform: scale(.99); box-shadow: 0 0 0 6px rgba(201, 169, 78, .3); }
/* Mystics are squares. */
.sqm { width: 40px; height: 40px; margin: 8px 12px 8px 8px; border-radius: 9px; overflow: hidden; flex: none; border: 1px solid rgba(231, 201, 131, .85); background: var(--plum); }
.sqm img, .por img { width: 100%; height: 100%; object-fit: cover; }
.sqm-ph { display: grid; place-items: center; width: 100%; height: 100%; font: 600 16px/1 var(--display); color: #e7c983; }
.yt-b-t { padding-right: 18px; font: 700 17px var(--ui); white-space: nowrap; }
.yt-price { align-self: stretch; display: flex; align-items: center; padding: 0 18px; border-left: 1px solid rgba(231, 201, 131, .45);
  background: rgba(13, 5, 32, .22); font: 700 16px var(--ui); color: #ffe9b8; font-variant-numeric: tabular-nums; white-space: nowrap; }
.yt-talk {
  display: block; margin: 10px auto 0; min-height: 44px; padding: 0 6px; border: 0; background: none; cursor: pointer; font: 500 15px var(--ui); color: var(--amethyst);
  text-decoration: underline; text-decoration-color: rgba(78, 39, 118, .35); text-underline-offset: 4px;
}
.yt-talk:hover { text-decoration-color: currentColor; }
.yt-talk:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; border-radius: 4px; }

/* E3: the sharer's ending, on the night block. */
.own { padding: 30px 22px; color: #f6edff; text-align: left; background: radial-gradient(120% 140% at 0% 0%, #3a1d63 0%, #1d0d38 55%, #12072a 100%); }
.own-n { margin: 0; font: 600 30px/1.15 var(--serif); color: #fff6e4; text-wrap: balance; }
.own-d { margin: 8px 0 0; font: italic 500 20px/1.3 var(--serif); color: #e6dbf6; }
.own-b {
  display: flex; align-items: center; gap: 12px; width: 100%; min-height: 60px; margin-top: 20px; padding: 8px 20px 8px 8px; border-radius: 16px; cursor: pointer; text-align: left;
  border: 1px solid rgba(231, 201, 131, .6); background: linear-gradient(180deg, #8250bd, #5d3194); color: #ffe9b8; font: 700 17px var(--ui);
  box-shadow: 0 14px 30px -14px rgba(140, 90, 210, .95);
}
.own-b:hover { filter: brightness(1.08); }
.own-b:focus-visible { outline: 2px solid #e7c983; outline-offset: 3px; }
.por { width: 44px; height: 44px; border-radius: 10px; overflow: hidden; flex: none; border: 1px solid rgba(231, 201, 131, .6); background: var(--plum); }

@container (min-width: 760px) {
  .yt { margin: 0 72px; padding-bottom: 44px; }
  .yt-h { font-size: 38px; }
  .yt-line { font-size: 18.5px; }
  .own { padding: 46px 72px 40px; }
  .own-n { font-size: 36px; }
  .own-b { display: inline-flex; width: auto; padding-right: 28px; font-size: 18px; }
}
@media (max-width: 359px) {
  .yt-b-t { padding-right: 12px; font-size: 16px; }
  .yt-price { padding: 0 12px; }
}
.yt-mine {
  display: inline-flex; align-items: center; gap: 14px; max-width: 100%; min-height: 64px; padding: 8px 18px 8px 8px;
  border-radius: 16px; cursor: pointer; text-align: left; border: 1px solid rgba(201, 169, 78, .55); background: #fffaf0; color: var(--ink);
  box-shadow: 0 10px 24px -16px rgba(78, 39, 118, .7); transition: border-color .2s, box-shadow .2s, transform .2s;
}
.yt-mine:hover { border-color: var(--gold); box-shadow: 0 12px 26px -14px rgba(78, 39, 118, .8); }
.yt-mine:focus-visible { outline: 2px solid var(--gold); outline-offset: 3px; }
.yt-mine.lit { transform: scale(.99); }
.yt-thumb { width: 38px; height: 62px; flex: none; border-radius: 5px; overflow: hidden; border: 1px solid rgba(201, 169, 78, .7); background: var(--plum); }
.yt-thumb img { width: 100%; height: 100%; object-fit: cover; }
.yt-thumb.rev img { transform: rotate(180deg); }
.yt-thumb-ph { display: grid; place-items: center; width: 100%; height: 100%; color: #e7c983; font-size: 14px; }
.yt-mine-t { font: 600 19px/1.2 var(--serif); color: var(--ink); }
.yt-mine-k { font: 600 12px/1 var(--ui); letter-spacing: .08em; text-transform: uppercase; color: var(--golddk); margin-right: 4px; }
.yt-mine-go { margin-left: 2px; font: 400 26px/1 var(--serif); color: var(--golddk); transform: translateY(-2px); transition: transform .2s; }
.yt-mine:hover .yt-mine-go { transform: translate(3px, -2px); }
@media (prefers-reduced-motion: reduce) { .yt-btn, .yt-mine { transition: none; } .yt-btn.lit, .yt-mine.lit { transform: none; } }
</style>
